"use client"

import { ArrowLeft, ArrowRight } from "@untitledui/icons"
import type { Transition, Variants } from "motion/react"
import { AnimatePresence, motion } from "motion/react"
import { type ReactNode, useState } from "react"

import { Avatar } from "@/components/ui/avatar"
import { cn, focalPointStyle } from "@/lib/utils"
import { collabPath } from "@/routes"
import type { Brief, BriefContributor } from "@/types"

type BriefCarouselItem = Pick<
  Brief,
  | "id"
  | "slug"
  | "cover_image_url"
  | "cover_image_focal_point"
  | "title"
  | "subtitle"
  | "main_headline"
  | "contributors_count"
  | "suggestions_count"
  | "featured_contributors"
  | "track_contributors"
> & {
  brief_contributors?: BriefContributor[]
}

interface SplitQuoteCarouselProps {
  briefs?: BriefCarouselItem[]
  /** Shown in the sidebar when `collabs` is empty (e.g. a static image). */
  fallback: ReactNode
}

interface CarouselSlideProps {
  slideKey: number
  direction: number
  delay?: number
  className?: string
  children: ReactNode
}

interface CarouselNavProps {
  onPrev: () => void
  onNext: () => void
}

interface CarouselDotsProps {
  count: number
  activeIndex: number
  onSelect: (index: number) => void
}

const variants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
  }),
  center: { x: 0, zIndex: 1, opacity: 1 },
  exit: (direction: number) => ({
    x: direction < 0 ? 500 : -500,
    zIndex: 0,
    opacity: 0,
  }),
}

const transition: Transition = {
  type: "tween",
  duration: 0.8,
  ease: [0.8, 0, 0.2, 1],
}

const MAX_AVATARS = 3

/**
 * The SplitQuoteCarousel component is a carousel of briefs.
 * @param briefs - The briefs to display in the carousel.
 * @param fallback - The fallback content to display when the carousel is empty.
 * @returns The SplitQuoteCarousel component.
 */
export function SplitQuoteCarousel({
  briefs = [],
  fallback,
}: SplitQuoteCarouselProps) {
  const [{ page, direction, count }, setPage] = useState({
    page: 0,
    direction: 0,
    count: 0,
  })

  const navigate = (dir: number) =>
    setPage((s) => ({
      page: s.page + dir,
      direction: dir,
      count: s.count + 1,
    }))

  if (briefs.length === 0) return fallback

  const currentIndex = wrap(0, briefs.length, page)
  const brief = briefs[currentIndex]

  // Jump to an absolute slide via the progress dots, animating in the
  // shortest linear direction from the current slide.
  const goTo = (index: number) => {
    if (index === currentIndex) return
    const delta = index - currentIndex
    setPage((s) => ({
      page: s.page + delta,
      direction: delta > 0 ? 1 : -1,
      count: s.count + 1,
    }))
  }

  const topContributors = [
    ...brief.featured_contributors,
    ...brief.track_contributors,
  ].slice(0, MAX_AVATARS)

  return (
    <div className="relative flex size-full flex-col items-start justify-end overflow-hidden rounded-3xl shadow-lg">
      {brief.cover_image_url ? (
        <img
          src={brief.cover_image_url}
          className="absolute inset-0 size-full object-cover transition-all duration-500"
          style={focalPointStyle(brief.cover_image_focal_point)}
          alt={brief.title}
        />
      ) : (
        <div className="bg-brand-50 absolute inset-0" />
      )}

      <div className="from-batyr-navy/80 via-batyr-navy/30 relative z-10 w-full bg-linear-to-t to-transparent pt-20">
        <div className="bg-batyr-navy/30 flex h-max flex-col gap-5 overflow-hidden border-t border-white/15 px-6 pt-6 pb-6 backdrop-blur-md">
          <CarouselSlide
            slideKey={count}
            direction={direction}
            delay={0.02}
            className="flex flex-col gap-3"
          >
            <a
              href={collabPath(brief.slug)}
              className="text-display-sm font-bold tracking-wide text-balance text-white uppercase transition-opacity hover:opacity-90"
            >
              {brief.title}
            </a>
            <p className="text-secondary_on-brand text-md font-light transition-opacity hover:opacity-90">
              {brief.subtitle}
            </p>
          </CarouselSlide>

          <div className="flex flex-col gap-4">
            <CarouselSlide slideKey={count} direction={direction} delay={0.04}>
              <div className="flex items-center gap-3 pt-1">
                {topContributors.length > 0 && (
                  <div className="flex items-center -space-x-1.5">
                    {topContributors.map((contributor) => (
                      <Avatar
                        key={contributor.id}
                        name={contributor.display_name}
                        avatarUrl={contributor.user?.avatar_url}
                        size="xs"
                      />
                    ))}
                  </div>
                )}
                <span className="text-tertiary_on-brand text-xs">
                  {brief.suggestions_count}{" "}
                  {plural(brief.suggestions_count, "suggestion")}
                  <span aria-hidden="true" className="px-1.5">
                    ·
                  </span>
                  {brief.contributors_count}{" "}
                  {plural(brief.contributors_count, "contributor")}
                </span>
              </div>
            </CarouselSlide>

            <div className="flex items-center justify-between gap-4">
              <CarouselDots
                count={briefs.length}
                activeIndex={currentIndex}
                onSelect={goTo}
              />
              <CarouselNav
                onPrev={() => navigate(-1)}
                onNext={() => navigate(1)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The CarouselSlide component is a fragment of the carousel.
 * @param slideKey - The key of the slide.
 * @param direction - The direction of the slide.
 * @param delay - The delay of the slide.
 * @param className - The class name of the slide.
 * @param children - The children of the slide.
 * @returns The CarouselSlide component.
 */
function CarouselSlide({
  slideKey,
  direction,
  delay = 0,
  className,
  children,
}: CarouselSlideProps) {
  return (
    <AnimatePresence initial={false} mode="popLayout" custom={direction}>
      <motion.div
        key={slideKey}
        custom={direction}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ ...transition, delay }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

/**
 * The CarouselDots component shows the carousel position and lets the user
 * jump to any brief. The active dot is a Batyr-yellow pill.
 * @param count - Total number of briefs.
 * @param activeIndex - Index of the currently shown brief.
 * @param onSelect - Called with the target index when a dot is clicked.
 * @returns The CarouselDots component.
 */
function CarouselDots({ count, activeIndex, onSelect }: CarouselDotsProps) {
  if (count <= 1) return null

  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: count }).map((_, index) => {
        const active = index === activeIndex
        return (
          <button
            key={index}
            type="button"
            aria-label={`Go to brief ${index + 1}`}
            aria-current={active}
            onClick={() => onSelect(index)}
            className={cn(
              "outline-focus-ring h-1.5 cursor-pointer rounded-full transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2",
              active
                ? "bg-batyr-yellow w-5"
                : "w-1.5 bg-white/40 hover:bg-white/70",
            )}
          />
        )
      })}
    </div>
  )
}

/**
 * The CarouselNav component is a fragment of the carousel.
 * @param onPrev - The function to call when the previous brief is clicked.
 * @param onNext - The function to call when the next brief is clicked.
 * @returns The CarouselNav component.
 */
function CarouselNav({ onPrev, onNext }: CarouselNavProps) {
  const styles = {
    navIcon:
      "text-batyr-navy transition-inherit-all size-4 group-hover:opacity-70 md:size-5 hover:cursor-pointer",
    navButton:
      "group outline-focus-ring bg-batyr-yellow hover:bg-brand-solid_hover active:bg-brand-solid_hover hover:border-batyr-yellow/50 border-batyr-yellow flex size-10 cursor-pointer items-center justify-center rounded-full border transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2 md:size-12",
  }

  return (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        aria-label="Previous brief"
        onClick={onPrev}
        className={styles.navButton}
      >
        <ArrowLeft className={styles.navIcon} />
      </button>
      <button
        type="button"
        aria-label="Next brief"
        onClick={onNext}
        className={styles.navButton}
      >
        <ArrowRight className={styles.navIcon} />
      </button>
    </div>
  )
}

/*
 * Helper functions for the carousel.
 */
function wrap(min: number, max: number, v: number) {
  const range = max - min
  return ((((v - min) % range) + range) % range) + min
}

function plural(count: number, word: string) {
  return count === 1 ? word : `${word}s`
}
